import {skipToken} from '@reduxjs/toolkit/query';
import {Card, DataStatus, FormSubmitHandler, showNotification} from 'platform/components';
import {Box, HStack, Show, Space, VStack} from 'platform/foundation';

import {useCallback, useEffect, useState} from 'react';
import {FieldErrors} from 'react-hook-form';
import {useParams} from 'react-router-dom';

import {isNotNil} from 'ramda';

import {suffixTestId, TestIdProps, useNavigate} from 'shared/web';

import {PageBackgroundGradient} from '../../components/PageBackgroundGradient/PageBackgroundGradient';
import {StickyContainer} from '../../components/StickyContainer/StickyContainer';
import i18n from '../../i18n/i18n';
import {
  useCreatePriceReportFromRequestMutation,
  useGetRequestQuery,
  useUpdateRequestMutation,
} from '../../store/priceReportApi';
import {getOverviewPath} from '../../utils/routing';
import {BottomActions} from './components/BottomActions';
import {CreatePageContext} from './components/CreatePageContext';
import {FeatureLevelSidebar} from './components/FeatureLevelSidebar';
import {Stepper} from './components/Stepper';
import {VehicleFeatures} from './components/VehicleFeatures';
import {VehicleFields} from './components/VehicleFields';
import {VehicleHeader} from './components/VehicleHeader';
import {WelcomeSidebar} from './components/WelcomeSidebar';
import {VehicleFormType} from './types/VehicleFormType';
import {useGetRequestVehicleFromFormData} from './utils/useGetRequestVehicleFromFormData';

export function CreatePage(props: TestIdProps) {
  const params = useParams();
  const navigate = useNavigate();
  const getRequestVehicleFromFormData = useGetRequestVehicleFromFormData();

  const {
    data: request,
    isError,
    isLoading,
    refetch: refetchRequest,
  } = useGetRequestQuery(isNotNil(params.requestId) ? {id: params.requestId} : skipToken);
  const [createPriceReport] = useCreatePriceReportFromRequestMutation();
  const [updateRequest] = useUpdateRequestMutation({fixedCacheKey: 'create-page-update'});
  const [step, setStep] = useState(0);

  useEffect(
    function scrollToTopOnStepChange() {
      window.scrollTo(0, 0);
    },
    [step]
  );

  const onSubmit: FormSubmitHandler<VehicleFormType> = async (data) => {
    await updateRequest({
      id: params.requestId ?? '',
      body: {vehicle: getRequestVehicleFromFormData(data, request)},
    })
      .unwrap()
      .catch(() => showNotification.error(i18n.t('general.labels.error')));

    if (step === 0) {
      return setStep(1);
    }

    await createPriceReport({body: {idRequest: params.requestId ?? ''}})
      .unwrap()
      .then(() =>
        // Ensure the navigation is not done before the request is refetched
        refetchRequest()
          .unwrap()
          .then(() => {
            navigate(getOverviewPath(params));
          })
      )
      .catch(() => showNotification.error(i18n.t('general.labels.error')));
  };

  const onChange = useCallback(
    (data: VehicleFormType) => {
      updateRequest({
        id: params.requestId ?? '',
        body: {vehicle: getRequestVehicleFromFormData(data, request)},
      })
        .unwrap()
        .catch(() => showNotification.error(i18n.t('general.labels.error')));
    },
    [updateRequest, params.requestId, getRequestVehicleFromFormData, request]
  );

  const onInvalidSubmit = (errors: FieldErrors<VehicleFormType>) => {
    const errorNames = Object.keys(errors);
    if (errorNames.length > 0) {
      const input = document.querySelector(`[data-name=${errorNames[0]}]`);
      input?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'start',
      });
    }
  };

  return (
    <PageBackgroundGradient data-testid={suffixTestId('wrapper', props)}>
      <DataStatus isLoading={isLoading} isError={isError}>
        <VStack shrink={1} maxWidth={315} width="100%">
          <CreatePageContext
            key={params.requestId ?? 'new'}
            onSubmit={onSubmit}
            onInvalidSubmit={onInvalidSubmit}
            onDebouncedChange={onChange}
            vehicle={request?.vehicle}
          >
            <Stepper
              steps={[
                i18n.t('page.create.labels.stepSpecification'),
                i18n.t('page.create.labels.stepFeatures'),
                i18n.t('page.create.labels.stepPricing'),
              ]}
              value={step}
              data-testid={suffixTestId('stepper', props)}
            />
            <Space vertical={6} />
            <VehicleHeader data-testid={suffixTestId('vehicleHeader', props)} />
            <Space vertical={6} />
            <HStack spacing={6} justify="center" wrap={[true, false, false, false]}>
              <Box flex={1}>
                <Card>
                  <Box padding={2}>
                    <Show when={step === 0}>
                      <VehicleFields data-testid={suffixTestId('vehicleFields', props)} />
                    </Show>
                    <Show when={step === 1}>
                      <VehicleFeatures data-testid={suffixTestId('vehicleFeatures', props)} />
                    </Show>
                  </Box>
                </Card>
              </Box>
              <Box width={['100%', 80, 80, 108]} flexShrink={0}>
                <StickyContainer offset={2}>
                  <Card>
                    <Box padding={2}>
                      <Show when={step === 0}>
                        <WelcomeSidebar
                          requestId={params.requestId}
                          data-testid={suffixTestId('welcomeSidebar', props)}
                        />
                      </Show>
                      <Show when={step === 1}>
                        <FeatureLevelSidebar
                          featureLevel={request?.featuresLevel ?? undefined}
                          isLoading={isLoading}
                          isError={isError}
                        />
                      </Show>
                    </Box>
                  </Card>
                </StickyContainer>
              </Box>
            </HStack>
            <BottomActions
              step={step}
              onBackClick={() => setStep(0)}
              data-testid={suffixTestId('bottomActions', props)}
            />
          </CreatePageContext>
        </VStack>
      </DataStatus>
    </PageBackgroundGradient>
  );
}
